import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";

const steps = [
  {
    number: "01",
    title: "Reach Out",
    description: "Contact me for a free 15-minute phone consultation. We'll talk about what's bringing you to therapy and whether we'd be a good fit.",
  },
  {
    number: "02",
    title: "First Session",
    description: "Our initial session is about getting to know each other. I'll ask about your history, your goals, and what you're hoping to get from therapy. There's no pressure — just an open conversation.",
  },
  {
    number: "03",
    title: "Building Together",
    description: "From there, we'll develop a plan that fits your needs. Sessions are typically weekly, and we'll regularly check in on how things feel as we go.",
  },
];

const WhatToExpect = () => {
  return (
    <section id="what-to-expect" className="what-to-expect-section py-24 px-6 bg-background">
      <div className="max-w-4xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2 className="what-to-expect-heading text-3xl md:text-4xl font-heading font-medium text-foreground mb-4">
            What to Expect
          </h2>
          <div className="w-16 h-px bg-accent mx-auto" />
        </motion.div>

        {/* Timeline */}
        <div className="what-to-expect-steps relative pl-12 md:pl-16">
          {/* Wandering dotted connector line */}
          <svg
            className="absolute left-[18px] md:left-[22px] top-2 w-12 -translate-x-1/2 overflow-visible"
            style={{ height: 'calc(100% - 56px)' }}
            viewBox="0 0 24 200"
            preserveAspectRatio="none"
            fill="none"
          >
            <path
              d="M 12 0 C 22 15, 2 30, 12 50 C 22 70, 4 80, 12 100 C 20 120, 2 135, 12 150 C 22 170, 4 185, 12 200"
              className="stroke-accent/30"
              strokeWidth="1.5"
              strokeDasharray="4 4"
              strokeLinecap="round"
              vectorEffect="non-scaling-stroke"
            />
          </svg>

          <div className="space-y-14">
            {steps.map((step, index) => (
              <motion.div
                key={step.number}
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="expect-step relative"
              >
                {/* Circle on timeline */}
                <div className="absolute -left-12 md:-left-16 top-1 w-9 h-9 md:w-11 md:h-11 rounded-full border-2 border-accent bg-background flex items-center justify-center">
                  <span className="expect-step-number text-xs md:text-sm font-heading font-medium text-accent">
                    {step.number}
                  </span>
                </div>
                <h3 className="expect-step-title text-xl font-heading font-medium text-foreground mb-2">
                  {step.title}
                </h3>
                <p className="font-body text-muted-foreground font-light leading-relaxed">
                  {step.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 10 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.3 }}
          className="what-to-expect-cta text-center mt-14"
        >
          <Link to="/what-to-expect" aria-label="Learn more about what to expect in therapy">
            <Button variant="outline" className="what-to-expect-learn-more font-body font-medium tracking-wider text-xs uppercase px-8">
              Learn More About What to Expect
            </Button>
          </Link>
        </motion.div>
      </div>
    </section>
  );
};

export default WhatToExpect;
